import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { toast } from '@livable/saas-react-components';
import { useState } from 'react';
import { api } from 'src/api';
import { downloadDialog, downloadErrorMessage, triggerDownload } from './billsDownload';

// Direct API calls rather than a store action: a download is one person's action, not state the page keeps.
const NO_OPTIONS = {
  cancelToken: {}
};
const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (_unused) {
    return undefined;
  }
};

/**
 * The Download button's work (LB-6033): ask what a download would hold, say what needs saying, then prepare it. A
 * download that is ready starts straight away; a large zip is emailed, and the person is told so.
 *
 * @param companyId the company the page is showing
 * @param requestParams the list's own request, whose order the download keeps
 * @param billIds the bills the footer's Download covers: the selection, or the page's Processed bills when nothing is
 * selected
 * @param includeApPush whether the CSV carries the AP Push column, as the page does for an AppFolio company
 */
export function useBillsDownload(_ref) {
  var _pending$dialog;
  let companyId = _ref.companyId,
    requestParams = _ref.requestParams,
    billIds = _ref.billIds,
    includeApPush = _ref.includeApPush;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    pending = _useState2[0],
    setPending = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isBusy = _useState4[0],
    setIsBusy = _useState4[1];
  const requestFor = (kind, ids) => ({
    companyId,
    kind,
    billIds: ids,
    sortBy: requestParams === null || requestParams === void 0 ? void 0 : requestParams.sortBy,
    sortDescending: requestParams === null || requestParams === void 0 ? void 0 : requestParams.sortDescending,
    timeZone: browserTimeZone(),
    includeApPush
  });
  const create = async request => {
    var _await$api$BillAutoma;
    const result = (_await$api$BillAutoma = await api.BillAutomationBills.createBillsExport(request, NO_OPTIONS)) === null || _await$api$BillAutoma === void 0 ? void 0 : _await$api$BillAutoma.response;
    if ((result === null || result === void 0 ? void 0 : result.status) === 'ready' && result.url) {
      triggerDownload(result.url);
    } else if ((result === null || result === void 0 ? void 0 : result.status) === 'queued') {
      toast('success', {
        title: "We'll email you a link",
        body: ["Your zip of ".concat(result.fileCount, " files is being prepared. We'll email you a link when it's ready.")]
      });
    } else {
      var _result$status;
      // Neither a download nor an email on its way: said, rather than leaving the person waiting on nothing.
      throw new Error("The download came back ".concat((_result$status = result === null || result === void 0 ? void 0 : result.status) !== null && _result$status !== void 0 ? _result$status : 'empty', "."));
    }
  };
  const run = async work => {
    setIsBusy(true);
    try {
      await work();
    } catch (err) {
      toast('error', {
        title: "Couldn't download the bills",
        body: [downloadErrorMessage(err)]
      });
    } finally {
      setIsBusy(false);
    }
  };

  /**
   * Asks what the download would hold first, so nothing is prepared that the person would not have wanted.
   *
   * @param ids the bills to download instead of the footer's: one row's, or the bill open in its details
   */
  const start = function (kind) {
    let ids = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : billIds;
    return run(async () => {
      var _await$api$BillAutoma2;
      const request = requestFor(kind, ids);
      const preview = (_await$api$BillAutoma2 = await api.BillAutomationBills.previewBillsExport(request, NO_OPTIONS)) === null || _await$api$BillAutoma2 === void 0 ? void 0 : _await$api$BillAutoma2.response;
      if (!preview) throw new Error('No preview');
      const dialog = downloadDialog(preview, kind, ids.length);
      if (dialog) {
        // The request is kept as it was asked, so confirming prepares exactly what the dialog described.
        setPending({
          request,
          dialog
        });
        return;
      }
      await create(request);
    });
  };
  const confirm = () => {
    if (!pending) return undefined;
    const request = pending.request,
      dialog = pending.dialog;
    setPending(null);
    return dialog.canConfirm ? run(() => create(request)) : undefined;
  };
  return {
    start,
    confirm,
    cancel: () => setPending(null),
    dialog: (_pending$dialog = pending === null || pending === void 0 ? void 0 : pending.dialog) !== null && _pending$dialog !== void 0 ? _pending$dialog : null,
    isBusy
  };
}